import type { CharacterAi, Manifest } from "../manifest/schema.js";
import { signOffLine } from "./guard.js";

/** One place on a lift floor. Detailed `facts` only for characters who operate the lift; brief for everyone else. */
export interface Floor {
  locationId: string;
  badge: string;
  label: string;
  /** Facts about the floor taken verbatim from the content (alt text, shop copy, info plaques, products). */
  facts: string[];
}

/** A place this character may take the guest to (travel tool). */
export interface Guide {
  locationId: string;
  label: string;
  badge?: string;
  /** True while the place is soft-deleted: never offered to the model and never travelled to. */
  archived: boolean;
}

/** Everything an AI character may state as fact, plus how they sound. Built only from manifest content, so it never drifts. */
export interface Grounding {
  characterId: string;
  name: string;
  role: string;
  /** False while the character's own location/shop is archived (they are not on the site until restored). */
  active: boolean;
  mallName: string;
  mallTagline: string;
  hereLocationId: string;
  hereName: string;
  persona: { summary: string; voice: string; traits: string[]; backstory: string; style: string };
  rules: { knows: string[]; avoids: string[]; deflection: string; greeting: string };
  voice: NonNullable<CharacterAi["voice"]>;
  /** What this character may state: their place, shop, products, scripted lines, authored facts. */
  facts: string[];
  /** The building directory (lift floors); detailed for lift operators. */
  floors: Floor[];
  guides: Guide[];
  /** Exact lines the worker may voice without a signature (greeting, scripted lines, deflection, sign-off). */
  lines: string[];
}

const pounds = (pence: number): string => `£${String(Math.round(pence / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
const norm = (s: string) => s.toLowerCase().replace(/^the /, "").trim();

/** Build grounding for a character. `m` is the manifest the character lives in; `building` (default `m`) is the current, archive-applied building. */
export function buildGrounding(m: Manifest, characterId: string, building: Manifest = m): Grounding {
  const c = m.characters.find((x) => x.id === characterId);
  if (!c) throw new Error(`unknown character ${characterId}`);
  if (!c.ai) throw new Error(`character ${characterId} has no ai block`);
  const ai = c.ai;
  const banned = (m.archivedNames ?? []).map(norm).filter((n) => n.length > 3);
  const mentionsArchived = (t: string) => banned.some((n) => t.toLowerCase().includes(n));
  const keep = (t: string) => !mentionsArchived(t);
  const bannedB = (building.archivedNames ?? []).map(norm).filter((n) => n.length > 3);
  const keepB = (t: string) => !bannedB.some((n) => t.toLowerCase().includes(n));

  const here = m.locations.find((l) => l.id === c.locationId);
  const shop = c.shopId ? m.shops.find((s) => s.id === c.shopId) : here?.shopId ? m.shops.find((s) => s.id === here.shopId) : undefined;
  const shopLocs = shop ? m.locations.filter((l) => l.shopId === shop.id) : here ? [here] : [];

  const facts: string[] = [];
  const add = (t: string | undefined) => {
    const v = t?.replace(/\s+/g, " ").trim();
    if (v && keep(v) && !facts.includes(v)) facts.push(v);
  };
  for (const f of ai.facts) add(f);
  if (here) add(`${here.name}: ${here.alt}`);
  for (const l of shopLocs) {
    if (l.id !== here?.id) add(`${l.name}: ${l.alt}`);
    for (const h of l.hotspots) if (h.kind === "info") add(h.text);
  }
  if (here) for (const h of here.hotspots) if (h.kind === "info") add(h.text);
  if (shop) {
    add(`${shop.name}: ${shop.tagline}. ${shop.description}`);
    for (const p of m.products.filter((p) => p.shopId === shop.id))
      add(`${p.name}: ${p.description}${p.material ? ` Material: ${p.material}.` : ""} ${p.pricePence ? `Price ${pounds(p.pricePence)}.` : "Price on request."}`);
  }
  const lines: string[] = [];
  const addLine = (t: string | undefined) => {
    const v = t?.trim();
    if (v && !lines.includes(v)) lines.push(v);
  };
  const startSay = c.script.nodes[c.script.start]?.say;
  const greeting = ai.greeting ?? startSay ?? `Good afternoon. I am ${c.name.split(" ")[0]}. How may I help?`;
  addLine(greeting);
  for (const n of Object.values(c.script.nodes)) {
    addLine(n.say);
    if (keep(n.say)) add(n.say);
  }
  // The mystery's teller may voice its lines verbatim (they are authored content, see docs/MYSTERY.md).
  const mystery = building.mall.mystery ?? m.mall.mystery;
  if (mystery && mystery.characterId === c.id) for (const l of mystery.lines) addLine(l);
  addLine(ai.deflection);
  addLine(signOffLine(c.name.split(" ")[0] ?? c.name));

  // Building directory and guides come from the current building (archive applied), not from the character's own manifest.
  const lift = building.locations.find((l) => l.hotspots.some((h) => h.kind === "menu"));
  const menu = lift?.hotspots.find((h) => h.kind === "menu");
  const detailed = here?.hotspots.some((h) => h.kind === "menu") ?? false;
  const floors: Floor[] = [];
  if (menu && menu.kind === "menu") {
    for (const item of menu.items) {
      const loc = building.locations.find((l) => l.id === item.to);
      if (!loc) continue;
      const bShop = loc.shopId ? building.shops.find((s) => s.id === loc.shopId) : undefined;
      const facts2: string[] = [];
      const addF = (t: string | undefined) => {
        const v = t?.replace(/\s+/g, " ").trim();
        if (v && keepB(v) && !facts2.includes(v)) facts2.push(v);
      };
      addF(loc.alt);
      if (bShop) addF(`${bShop.name}: ${bShop.tagline}. ${bShop.description}`);
      if (detailed) {
        const shopLocs2 = loc.shopId ? building.locations.filter((l) => l.shopId === loc.shopId) : [loc];
        for (const l of shopLocs2) {
          if (l.id !== loc.id) addF(`${l.name}: ${l.alt}`);
          for (const h of l.hotspots) if (h.kind === "info") addF(h.text);
        }
        if (bShop) for (const p of building.products.filter((p) => p.shopId === bShop.id)) addF(`${p.name}: ${p.description}`);
      }
      floors.push({ locationId: loc.id, badge: item.badge ?? "", label: item.label, facts: facts2 });
    }
  }

  const guides: Guide[] = [];
  const addGuide = (id: string, label?: string, badge?: string) => {
    if (guides.some((g) => g.locationId === id)) return;
    const full = m.locations.find((l) => l.id === id) ?? building.locations.find((l) => l.id === id);
    if (!full) return;
    guides.push({ locationId: id, label: label ?? full.name, ...(badge ? { badge } : {}), archived: !building.locations.some((l) => l.id === id) });
  };
  if (detailed) for (const f of floors) addGuide(f.locationId, f.label, f.badge);
  for (const id of ai.canGuide) {
    const f = floors.find((x) => x.locationId === id);
    addGuide(id, f?.label, f?.badge || undefined);
  }

  return {
    characterId: c.id,
    name: c.name,
    role: c.role,
    active: m === building || building.characters.some((x) => x.id === c.id),
    mallName: m.mall.name,
    mallTagline: m.mall.tagline,
    hereLocationId: c.locationId,
    hereName: here?.name ?? c.locationId,
    persona: { summary: c.persona.summary, voice: c.persona.voice, traits: c.persona.traits, backstory: ai.backstory, style: ai.style },
    rules: { knows: ai.knows, avoids: ai.avoids, deflection: ai.deflection, greeting },
    voice: ai.voice,
    facts,
    floors,
    guides,
    lines
  };
}

/** Ids of every character with an `ai` block: the characters that get the AI chat. */
export function aiCharacterIds(m: Manifest): string[] {
  return m.characters.filter((c) => c.ai).map((c) => c.id);
}

/** Grounding for every AI character in the full (restorable) manifest, using the current archive-applied building. */
export function buildAllGroundings(full: Manifest, visible: Manifest): Record<string, Grounding> {
  const out: Record<string, Grounding> = {};
  for (const id of aiCharacterIds(full)) {
    const inVisible = visible.characters.some((c) => c.id === id);
    out[id] = inVisible ? buildGrounding(visible, id) : buildGrounding(full, id, visible);
  }
  return out;
}
